import { useTranslation } from 'react-i18next';
import { Dropdown } from 'primereact/dropdown';
import { Label } from '@/components/ui/label';
import {
  getDefaultBankAccountId,
  isBankSettledMethod,
  shouldShowBankPicker,
  type BankAccountOption,
} from '@/lib/bank-accounts';

interface BankAccountFieldProps {
  method: string;
  accounts: readonly BankAccountOption[];
  /** Explicit choice; '' means "not chosen" and the preselected default is displayed/submitted. */
  value: string;
  onChange: (id: string) => void;
}

/**
 * Receiving-bank-account picker for bank-settled tenders (CARD, wallet, QR, BANK_TRANSFER).
 * Hidden when the tenant has <= 1 account (the server auto-uses the single account) so the
 * cashier adds no step; BANK_TRANSFER with no account configured shows the setup hint.
 */
export function BankAccountField({ method, accounts, value, onChange }: BankAccountFieldProps) {
  const { t } = useTranslation('pos');
  if (!isBankSettledMethod(method)) return null;

  if (accounts.length === 0) {
    return method === 'BANK_TRANSFER' ? (
      <p className="text-xs text-muted-foreground">
        {t('noBankAccounts')} {t('noBankAccountsHint')}
      </p>
    ) : null;
  }
  if (!shouldShowBankPicker(accounts)) return null;

  return (
    <div>
      <Label className="text-xs">
        {method === 'BANK_TRANSFER' ? t('bankAccountRequired') : t('receivingBankAccount')}
      </Label>
      <Dropdown
        value={value || getDefaultBankAccountId(accounts)}
        onChange={(e) => onChange(e.value as string)}
        options={accounts.map((acc) => ({ label: `${acc.name} — ${acc.bankName}`, value: acc.id }))}
        optionLabel="label"
        optionValue="value"
        placeholder={t('selectBankAccount')}
        className="mt-1 w-full"
      />
    </div>
  );
}
